/**
 * @file
 * TP Address composite behaviour.
 *
 * Enhances the postal-code and locality inputs rendered by the
 * `tp_address` webform composite with a Select2-style autocomplete
 * listbox. Replaces the previous native `<datalist>` approach with a
 * proper ARIA 1.2 combobox pattern (`role="combobox"` +
 * `role="listbox"` + `aria-activedescendant`) that is fully styleable
 * and cross-browser consistent.
 *
 * The two inputs are kept in sync: picking a postal code auto-fills
 * a single matching locality (and vice versa), mirroring the original
 * behaviour but with accessible keyboard navigation and announcements.
 */

interface Suggestion {
  value: string;
  label: string;
  postal_code?: string;
  locality?: string;
}

const TP_ADDRESS_DEBOUNCE_MS = 200;
const TP_ADDRESS_MIN_CHARS = 1;

/**
 * Debounce an async-ish callback.
 */
function tpAddressDebounce<T extends (...args: never[]) => void>(fn: T, wait: number): T {
  let timer: number | undefined;
  return ((...args: Parameters<T>) => {
    window.clearTimeout(timer);
    timer = window.setTimeout(() => fn(...args), wait);
  }) as T;
}

/**
 * Fetch suggestions from a JSON endpoint.
 */
async function tpAddressLoadSuggestions(endpoint: string, params: Record<string, string>): Promise<Suggestion[]> {
  try {
    const url = new URL(endpoint, window.location.origin);
    Object.entries(params).forEach(([key, value]) => {
      const trimmed = (value || '').trim();
      if (trimmed !== '') {
        url.searchParams.set(key, trimmed);
      }
    });

    const response = await fetch(url.toString(), { credentials: 'same-origin' });
    if (!response.ok) return [];

    const data = (await response.json()) as unknown;
    return Array.isArray(data) ? (data as Suggestion[]) : [];
  } catch {
    return [];
  }
}

/**
 * Find an exact case-insensitive match on the given key.
 */
function tpAddressFindExactMatch(suggestions: Suggestion[], value: string, key: keyof Suggestion): Suggestion | null {
  const normalized = value.trim().toLowerCase();
  if (normalized === '') return null;

  return (
    suggestions.find(
      (s) =>
        String(s[key] ?? '')
          .trim()
          .toLowerCase() === normalized
    ) ?? null
  );
}

/**
 * Enhances a single text input with a listbox autocomplete popup.
 *
 * @description
 * Applies the ARIA combobox pattern: the input gets `role="combobox"`,
 * `aria-autocomplete="list"`, `aria-expanded`, `aria-controls`, and
 * `aria-activedescendant`. A sibling `<ul role="listbox">` is created
 * to host the options. Keyboard interactions follow the WAI-ARIA
 * Authoring Practices for comboboxes.
 */
class TpAddressCombobox {
  public readonly input: HTMLInputElement;
  private readonly listbox: HTMLUListElement;
  private readonly listboxId: string;
  private readonly emptyMessage: string;
  private readonly onSelect: (suggestion: Suggestion) => void;
  private suggestions: Suggestion[] = [];
  private activeIndex = -1;

  constructor(input: HTMLInputElement, idSuffix: string, onSelect: (s: Suggestion) => void) {
    this.input = input;
    this.onSelect = onSelect;
    this.emptyMessage = Drupal.t('No matches found');

    const baseId = input.id || `tp-address-${idSuffix}-${Math.random().toString(36).slice(2, 8)}`;
    this.listboxId = `${baseId}--listbox`;

    // Remove any legacy `<datalist>` left over from previous behaviour.
    const legacyList = input.getAttribute('list');
    if (legacyList) {
      document.getElementById(legacyList)?.remove();
      input.removeAttribute('list');
    }

    // Apply combobox semantics.
    input.classList.add('tp-address__input');
    input.setAttribute('role', 'combobox');
    input.setAttribute('aria-autocomplete', 'list');
    input.setAttribute('aria-expanded', 'false');
    input.setAttribute('aria-controls', this.listboxId);
    input.setAttribute('autocomplete', input.getAttribute('autocomplete') || 'off');
    input.setAttribute('spellcheck', 'false');

    // Use the label text as a placeholder (labels are visually hidden).
    const labelText = input.labels?.[0]?.textContent?.trim();
    if (labelText && !input.getAttribute('placeholder')) {
      input.setAttribute('placeholder', labelText);
    }

    // Build listbox.
    this.listbox = document.createElement('ul');
    this.listbox.id = this.listboxId;
    this.listbox.className = 'tp-address__listbox';
    this.listbox.setAttribute('role', 'listbox');
    this.listbox.setAttribute('aria-label', input.labels?.[0]?.textContent?.trim() || Drupal.t('Suggestions'));
    this.listbox.hidden = true;
    input.insertAdjacentElement('afterend', this.listbox);

    this.bindEvents();
  }

  /** Replace the rendered options with a new set of suggestions. */
  public setSuggestions(suggestions: Suggestion[]): void {
    this.suggestions = suggestions;
    this.activeIndex = -1;
    this.render();
  }

  /** Open the listbox (no-op if already open or empty). */
  public open(): void {
    if (!this.listbox.hidden) return;
    if (this.listbox.childElementCount === 0) return;
    this.listbox.hidden = false;
    this.input.setAttribute('aria-expanded', 'true');
  }

  /** Close the listbox and clear keyboard highlight. */
  public close(): void {
    this.listbox.hidden = true;
    this.input.setAttribute('aria-expanded', 'false');
    this.input.removeAttribute('aria-activedescendant');
    this.setActiveIndex(-1);
  }

  /** Remove the listbox and ARIA augmentations. */
  public destroy(): void {
    this.listbox.remove();
    this.input.classList.remove('tp-address__input');
    ['role', 'aria-autocomplete', 'aria-expanded', 'aria-controls', 'aria-activedescendant'].forEach((attr) =>
      this.input.removeAttribute(attr)
    );
  }

  // ── Private ──────────────────────────────────────────────────────────
  private bindEvents(): void {
    this.input.addEventListener('keydown', this.handleKeydown);
    this.input.addEventListener('focus', this.handleFocus);
    this.input.addEventListener('blur', this.handleBlur);
    this.listbox.addEventListener('mousedown', this.handleListMouseDown);
    this.listbox.addEventListener('click', this.handleListClick);
  }

  private render(): void {
    this.listbox.replaceChildren();

    if (this.suggestions.length === 0) {
      const empty = document.createElement('li');
      empty.className = 'tp-address__empty';
      empty.setAttribute('role', 'presentation');
      empty.textContent = this.emptyMessage;
      this.listbox.appendChild(empty);
      this.listbox.hidden = false;
      this.input.setAttribute('aria-expanded', 'true');
      return;
    }

    const currentValue = this.input.value.trim().toLowerCase();

    this.suggestions.forEach((suggestion, index) => {
      const option = document.createElement('li');
      option.id = `${this.listboxId}-opt-${index}`;
      option.className = 'tp-address__option';
      option.setAttribute('role', 'option');
      option.dataset.index = String(index);
      option.textContent = suggestion.label || suggestion.value;

      if (suggestion.value.trim().toLowerCase() === currentValue) {
        option.setAttribute('aria-selected', 'true');
      }

      this.listbox.appendChild(option);
    });

    this.open();
  }

  private setActiveIndex(index: number): void {
    const options = this.listbox.querySelectorAll<HTMLLIElement>('.tp-address__option');
    options.forEach((opt) => opt.classList.remove('is-active'));

    this.activeIndex = index;

    if (index < 0 || index >= options.length) {
      this.input.removeAttribute('aria-activedescendant');
      return;
    }

    const active = options[index];
    active.classList.add('is-active');
    this.input.setAttribute('aria-activedescendant', active.id);
    active.scrollIntoView({ block: 'nearest' });
  }

  private selectByIndex(index: number): void {
    const suggestion = this.suggestions[index];
    if (!suggestion) return;
    this.input.value = suggestion.value;
    this.onSelect(suggestion);
    this.close();
    Drupal.announce(Drupal.t('@label selected', { '@label': suggestion.label || suggestion.value }));
  }

  private readonly handleKeydown = (event: KeyboardEvent): void => {
    const options = this.listbox.querySelectorAll<HTMLLIElement>('.tp-address__option');
    const isOpen = !this.listbox.hidden;

    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        if (!isOpen) {
          this.open();
          return;
        }
        if (options.length === 0) return;
        this.setActiveIndex((this.activeIndex + 1) % options.length);
        break;
      case 'ArrowUp':
        event.preventDefault();
        if (!isOpen) {
          this.open();
          return;
        }
        if (options.length === 0) return;
        this.setActiveIndex(this.activeIndex <= 0 ? options.length - 1 : this.activeIndex - 1);
        break;
      case 'Home':
        if (isOpen && options.length > 0) {
          event.preventDefault();
          this.setActiveIndex(0);
        }
        break;
      case 'End':
        if (isOpen && options.length > 0) {
          event.preventDefault();
          this.setActiveIndex(options.length - 1);
        }
        break;
      case 'Enter':
        if (isOpen && this.activeIndex >= 0) {
          event.preventDefault();
          this.selectByIndex(this.activeIndex);
        }
        break;
      case 'Escape':
        if (isOpen) {
          event.preventDefault();
          this.close();
        }
        break;
      case 'Tab':
        this.close();
        break;
      default:
        break;
    }
  };

  private readonly handleFocus = (): void => {
    if (this.suggestions.length > 0) {
      this.open();
    }
  };

  private readonly handleBlur = (): void => {
    // Delay close so option mousedown can still register.
    window.setTimeout(() => this.close(), 120);
  };

  // Prevent focus loss before click fires.
  private readonly handleListMouseDown = (event: MouseEvent): void => {
    event.preventDefault();
  };

  private readonly handleListClick = (event: MouseEvent): void => {
    const target = (event.target as HTMLElement).closest<HTMLLIElement>('.tp-address__option');
    if (!target || !target.dataset.index) return;
    this.selectByIndex(Number.parseInt(target.dataset.index, 10));
    this.input.focus();
  };
}

((Drupal, once) => {
  'use strict';

  /**
   * Attaches the combobox behaviour to every `tp-address` composite.
   *
   * @description
   * Reads the autocomplete endpoints from the SDC wrapper's data
   * attributes and wires up a debounced fetch for each input. When
   * exactly one suggestion matches, the companion field is auto-filled
   * (postal code ↔ locality) to match the historical UX.
   */
  Drupal.behaviors.tpAddress = {
    attach(context: Document | HTMLElement) {
      const wrappers = once('tp-address-composite', '[data-tp-address]', context);

      wrappers.forEach((wrapper) => {
        const root = wrapper as HTMLElement;
        const postalInput = root.querySelector<HTMLInputElement>('.js-tp-address-postal-code');
        const localityInput = root.querySelector<HTMLInputElement>('.js-tp-address-locality');
        const postalEndpoint = root.dataset.tpAddressPostalEndpoint || '';
        const localityEndpoint = root.dataset.tpAddressLocalityEndpoint || '';

        // Treat hidden / disabled inputs as "not present" so a single visible
        // field still gets its own autocomplete instead of the whole composite
        // being skipped.
        const isUsable = (el: HTMLInputElement | null): el is HTMLInputElement =>
          !!el && !el.disabled && el.type !== 'hidden';

        const usablePostal = isUsable(postalInput) ? postalInput : null;
        const usableLocality = isUsable(localityInput) ? localityInput : null;

        // Collapse the layout when one of the two composite fields is not
        // actually usable (disabled or hidden). Hiding the wrapper prevents
        // an empty column from reserving horizontal space and the `--solo`
        // modifier lets the remaining field occupy the full row width.
        const postalWrapper = postalInput?.closest<HTMLElement>('.tp-address__field--postal-code') ?? null;
        const localityWrapper = localityInput?.closest<HTMLElement>('.tp-address__field--locality') ?? null;

        if (postalWrapper && !usablePostal) {
          postalWrapper.hidden = true;
        }
        if (localityWrapper && !usableLocality) {
          localityWrapper.hidden = true;
        }
        if (usablePostal && !usableLocality) {
          postalWrapper?.classList.add('tp-address__field--solo');
        }
        if (usableLocality && !usablePostal) {
          localityWrapper?.classList.add('tp-address__field--solo');
        }

        // Nothing to do if neither field is usable or no endpoints are exposed.
        if ((!usablePostal || !postalEndpoint) && (!usableLocality || !localityEndpoint)) {
          return;
        }

        let postalCombo: TpAddressCombobox | null = null;
        let localityCombo: TpAddressCombobox | null = null;

        if (usablePostal && postalEndpoint) {
          postalCombo = new TpAddressCombobox(usablePostal, 'postal', (s) => {
            // Picking a suggestion is an explicit user action, so always sync
            // the companion field when it exists and is editable.
            if (s.locality && usableLocality) {
              usableLocality.value = s.locality;
              usableLocality.dispatchEvent(new Event('change', { bubbles: true }));
            }
          });
        }

        if (usableLocality && localityEndpoint) {
          localityCombo = new TpAddressCombobox(usableLocality, 'locality', (s) => {
            if (s.postal_code && usablePostal) {
              usablePostal.value = s.postal_code;
              usablePostal.dispatchEvent(new Event('change', { bubbles: true }));
            }
          });
        }

        const fetchPostal = postalCombo
          ? tpAddressDebounce(async () => {
              if (!usablePostal) return;
              if (usablePostal.value.trim().length < TP_ADDRESS_MIN_CHARS) {
                postalCombo!.setSuggestions([]);
                postalCombo!.close();
                return;
              }
              const params: Record<string, string> = { q: usablePostal.value };
              if (usableLocality) {
                params.locality = usableLocality.value;
              }
              const suggestions = await tpAddressLoadSuggestions(postalEndpoint, params);
              postalCombo!.setSuggestions(suggestions);

              // Cross-fill locality from the returned suggestions when the
              // sibling is editable. Exact match always wins (the user has
              // typed a full postal code); the single-suggestion shortcut only
              // fires when the locality field is still empty so we don't
              // overwrite something the user is actively typing.
              if (usableLocality) {
                const exact = tpAddressFindExactMatch(suggestions, usablePostal.value, 'postal_code');
                if (exact?.locality) {
                  usableLocality.value = exact.locality;
                  usableLocality.dispatchEvent(new Event('change', { bubbles: true }));
                } else if (suggestions.length === 1 && suggestions[0].locality && usableLocality.value.trim() === '') {
                  usableLocality.value = suggestions[0].locality;
                  usableLocality.dispatchEvent(new Event('change', { bubbles: true }));
                }
              }
            }, TP_ADDRESS_DEBOUNCE_MS)
          : null;

        const fetchLocality = localityCombo
          ? tpAddressDebounce(async () => {
              if (!usableLocality) return;
              if (usableLocality.value.trim().length < TP_ADDRESS_MIN_CHARS) {
                localityCombo!.setSuggestions([]);
                localityCombo!.close();
                return;
              }
              const params: Record<string, string> = { q: usableLocality.value };
              if (usablePostal) {
                params.postal_code = usablePostal.value;
              }
              const suggestions = await tpAddressLoadSuggestions(localityEndpoint, params);
              localityCombo!.setSuggestions(suggestions);

              if (usablePostal) {
                const exact = tpAddressFindExactMatch(suggestions, usableLocality.value, 'locality');
                if (exact?.postal_code) {
                  usablePostal.value = exact.postal_code;
                  usablePostal.dispatchEvent(new Event('change', { bubbles: true }));
                } else if (suggestions.length === 1 && suggestions[0].postal_code && usablePostal.value.trim() === '') {
                  usablePostal.value = suggestions[0].postal_code;
                  usablePostal.dispatchEvent(new Event('change', { bubbles: true }));
                }
              }
            }, TP_ADDRESS_DEBOUNCE_MS)
          : null;

        if (usablePostal && fetchPostal) {
          usablePostal.addEventListener('input', fetchPostal);
        }
        if (usableLocality && fetchLocality) {
          usableLocality.addEventListener('input', fetchLocality);
        }

        // Close any open listbox when clicking outside the composite.
        const handleOutsideClick = (event: MouseEvent): void => {
          if (!root.contains(event.target as Node)) {
            postalCombo?.close();
            localityCombo?.close();
          }
        };
        document.addEventListener('click', handleOutsideClick);

        // Stash cleanup hooks on the element for `detach`.
        (root as unknown as { _tpAddressCleanup?: () => void })._tpAddressCleanup = () => {
          document.removeEventListener('click', handleOutsideClick);
          postalCombo?.destroy();
          localityCombo?.destroy();
        };
      });
    },

    detach(context: Document | HTMLElement, _settings, trigger) {
      if (trigger !== 'unload') return;
      const root = context instanceof HTMLElement ? context : document;
      root.querySelectorAll<HTMLElement>('[data-tp-address]').forEach((el) => {
        const cleanup = (el as unknown as { _tpAddressCleanup?: () => void })._tpAddressCleanup;
        cleanup?.();
      });
    },
  };
})(Drupal, once);
